/* Copyright 2013 - 2024 Waiterio LLC */
/** @jsxImportSource @emotion/react */
import React from 'react'
import { css } from '@emotion/react'
import { useTranslation } from '@multilocale/react/index.jsx'
import IconCompass from '@stiloso/icons/IconCompass.jsx'
import IconEmail from '@stiloso/icons/IconEmail.jsx'
import IconFacebook from '@stiloso/icons/IconFacebook.jsx'
import IconLinkedin from '@stiloso/icons/IconLinkedin.jsx'
import IconTwitter from '@stiloso/icons/IconTwitter.jsx'

const footer = css({
  width: '100%',
  padding: '60px 0px 0px',
  backgroundColor: 'white',
})

const footerBox = css({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
})

const footerItem = css({
  display: 'flex',
  width: 262,
  padding: '0px 15px',
  marginBottom: 30,
})

const footerItemHeader = css({
  color: '#333',
  fontSize: 24,
  margin: 0,
  textTransform: 'capitalize',
})

const shareIcons = css({
  textAlign: 'center',
  width: 44,
  height: 44,
  border: 'solid #666 0.5px',
  borderRadius: 28,
  margin: '0px 5px',
  fill: '#666',
  cursor: 'pointer',

  ':hover': {
    background: '#3f92d2',
    borderColor: '#3f92d2',
    fill: 'white',
    transition: 'background 0.5s, fill 0.5s',
  },
})

const footerText = css({
  p: {
    marginTop: -4,
  },
})

const footerHorizontalBar = css({
  height: 1,
  opacity: 0.2,
  margin: '0px 60px 8px 60px',
})

const iconGrayCircle = css({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  width: 60,
  height: 60,
  marginRight: 20,
  backgroundColor: '#f4f4f4',
  borderRadius: '50%',
})

const FooterComponent = ({ locale }) => {
  const { t } = useTranslation(locale)

  return (
    <div css={footer}>
      <div css={footerBox}>
        <div css={footerItem}>
          <div css={[iconGrayCircle, { marginRight: 19.5 }]}>
            <IconCompass
              ariaHidden="true"
              css={{
                width: 26,
                height: 26,
                fill: '#3f92d2',
                marginBottom: 1,
              }}
            />
          </div>
          <div css={{ marginTop: -2 }}>
            <h4 css={[footerItemHeader, { paddingBottom: 17 }]}>
              {t('Address')}
            </h4>
            <div css={footerText}>
              <p>Waiterio LLC</p>
              <p>30 N Gould St</p>
              <p>Ste N</p>
              <p>Sheridan, WY 82801</p>
              <p>USA</p>
            </div>
          </div>
        </div>
        <div css={footerItem}>
          <div css={iconGrayCircle}>
            <IconEmail
              ariaHidden="true"
              css={{
                width: 24,
                height: 24,
                fill: '#3f92d2',
                marginBottom: 1,
              }}
            />
          </div>
          <div>
            <h4 css={[footerItemHeader, { marginTop: -2, paddingBottom: 11 }]}>
              {t('Contact us')}
            </h4>
            <span css={footerText}>info@waiterio.com</span>
          </div>
        </div>
        <div css={[footerItem, { flexDirection: 'column' }]}>
          <h4 css={[footerItemHeader, { marginTop: -2, paddingBottom: 8 }]}>
            {t('Follow Us')}
          </h4>
          <div css={{ display: 'flex' }}>
            <div
              css={shareIcons}
              onClick={() => {
                window.location.href = 'https://twitter.com/waiterio'
              }}
            >
              <IconTwitter
                ariaHidden="true"
                css={{ marginTop: 10, width: 18, height: 22 }}
              />
            </div>
            <div
              css={shareIcons}
              onClick={() => {
                window.location.href = 'https://www.facebook.com/waiterio'
              }}
            >
              <IconFacebook
                ariaHidden="true"
                css={{ marginTop: 12, width: 22, height: 22 }}
              />
            </div>
            <div
              css={shareIcons}
              onClick={() => {
                window.location.href =
                  'https://www.linkedin.com/company/waiter-io'
              }}
            >
              <IconLinkedin
                ariaHidden="true"
                css={{ marginTop: 12, width: 22, height: 22 }}
              />
            </div>
          </div>
        </div>
        <div css={[footerItem, { flexDirection: 'column' }]}>
          <h4 css={[footerItemHeader, { marginTop: -2, paddingBottom: 13 }]}>
            {t('More Links')}
          </h4>
          <div
            css={[
              footerText,
              { display: 'flex', flexDirection: 'column', color: '#3f92d2' },
            ]}
          >
            <a href="/privacy-policy">{t('Privacy Policy')}</a>
            <a href="/terms-and-conditions" css={{ marginTop: 10 }}>
              {t('Terms and Conditions')}
            </a>
          </div>
        </div>
      </div>
      <hr css={footerHorizontalBar} />
      <div
        css={{
          display: 'flex',
          justifyContent: 'center',
          padding: '20px 0px',
          marginTop: -5,
        }}
      >
        <span css={footerText}>
          {t('Copyright')} @{' '}
          <a href="/" css={{ color: '#3f92d2' }}>
            Waiterio
          </a>{' '}
          2013 - {new Date().getFullYear()}
        </span>
      </div>
    </div>
  )
}

export default FooterComponent
